import type { CSSProperties } from 'react';
import { useBreakpointValue } from '@chakra-ui/react';
import logo from 'utils/assets/img/logo.png';
import PrestazioneSection from './PrestazioneSection';
import StaticCheckbox from './StaticCheckbox';
import { displayableClientEmail, ReportFormState, SERVICE_RATING_OPTIONS } from '../types';

interface InterventionReportPrintViewProps {
  reportNumberFormatted: string;
  reportDate: string;
  taskTitle: string;
  compiledByLabel: string;
  employeeSignatureUrl: string | null;
  data: ReportFormState;
}

const sectionHeaderStyle: CSSProperties = {
  fontSize: '13px',
  fontWeight: 800,
  color: '#1a2942',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  padding: '3px 6px',
  background: '#eef2f7',
  borderBottom: '1px solid #94a3b8',
};

const fieldLabelStyle: CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: '#64748b',
  display: 'block',
  marginBottom: '1px',
};

const fieldValueStyle: CSSProperties = {
  width: '100%',
  fontSize: '14px',
  color: '#1a2942',
  borderBottom: '1px solid #94a3b8',
  padding: '2px 2px',
  minHeight: '17px',
  boxSizing: 'border-box',
};

export default function InterventionReportPrintView({
  reportNumberFormatted,
  reportDate,
  taskTitle,
  compiledByLabel,
  employeeSignatureUrl,
  data,
}: InterventionReportPrintViewProps) {
  const isMobile = Boolean(useBreakpointValue({ base: true, md: false }));
  const presentOperators = data.operators.filter(o => o.present);
  const otherOperators = data.operators.filter(o => !o.present);

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif", color: '#1a2942', border: '1px solid #94a3b8', background: 'white', width: '100%', boxSizing: 'border-box', boxShadow: '0 1px 6px rgba(26, 41, 66, 0.15)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 10px', borderBottom: '1px solid #94a3b8' }}>
        <img src={logo} alt="EGR SRL" height="22px" />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid #94a3b8' }}>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'center', padding: '4px 6px', background: '#37492e', color: '#ffffff', fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          Recupero Rifiuti
        </div>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'center', padding: '4px 6px', background: '#9ace38', color: '#1a2942', fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          Noleggio WC e Cassoni
        </div>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'center', padding: '4px 6px', background: '#5ea9f3', color: '#ffffff', fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          Autospurgo
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '1px solid #94a3b8', fontSize: '14px', fontWeight: 700, color: '#1a2942' }}>
        <span>FOGLIO DI LAVORO N. {reportNumberFormatted}</span>
        <span>DATA {reportDate}</span>
      </div>
      <div style={{ padding: '2px 10px', fontSize: '12px', color: '#64748b' }}>Intervento: {taskTitle}</div>

      {/* Cliente */}
      <div>
        <div style={sectionHeaderStyle}>Cliente</div>
        <div style={{ padding: '4px 10px' }}>
          <label style={fieldLabelStyle}>Cliente</label>
          <div style={fieldValueStyle}>{data.clientName}</div>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px', marginTop: '4px' }}>
            <div style={{ flex: isMobile ? undefined : '0 0 calc(45% - 5px)', minWidth: 0 }}>
              <label style={fieldLabelStyle}>Comune</label>
              <div style={fieldValueStyle}>{data.clientCity}</div>
            </div>
            <div style={{ flex: isMobile ? undefined : '0 0 calc(55% - 5px)', minWidth: 0 }}>
              <label style={fieldLabelStyle}>Indirizzo</label>
              <div style={fieldValueStyle}>{data.clientAddress}</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px', marginTop: '4px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={fieldLabelStyle}>C.F. / P.IVA</label>
              <div style={fieldValueStyle}>{data.clientTaxCode || data.clientVatNumber}</div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={fieldLabelStyle}>Tel.</label>
              <div style={fieldValueStyle}>{data.clientPhone}</div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={fieldLabelStyle}>Mail</label>
              <div style={fieldValueStyle}>{displayableClientEmail(data.clientEmail)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Prestazione */}
      <PrestazioneSection value={data.prestazione} onChange={() => {}} readOnly mobileLayout={isMobile} />

      {/* Allegati */}
      {data.attachments.length > 0 && (
        <div>
          <div style={sectionHeaderStyle}>Allegati</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '6px 10px' }}>
            {data.attachments.map(att => (
              <a key={att.id} href={att.previewUrl} target="_blank" rel="noreferrer">
                <img
                  src={att.previewUrl}
                  alt={att.fileName}
                  style={{ width: '70px', height: '70px', objectFit: 'cover', border: '1px solid #94a3b8', display: 'block' }}
                />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Note */}
      <div>
        <div style={sectionHeaderStyle}>Note</div>
        <div style={{ fontSize: '14px', color: '#1a2942', padding: '6px 10px', minHeight: '50px', whiteSpace: 'pre-wrap' }}>{data.notes}</div>
      </div>

      {/* Operatore / Presenti */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', borderBottom: '1px solid #94a3b8' }}>
        <div style={{ flex: 1, minWidth: 0, borderRight: isMobile ? 'none' : '1px solid #94a3b8', borderBottom: isMobile ? '1px solid #94a3b8' : 'none' }}>
          <div style={sectionHeaderStyle}>Operatore</div>
          <div style={{ padding: '4px 8px' }}>
            {[...presentOperators, ...otherOperators].map(op => (
              <div key={op.employeeId} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#1a2942', padding: '1px 0' }}>
                <StaticCheckbox checked={op.present} />
                <span style={{ minWidth: 0, wordBreak: 'break-word' }}>{op.firstName} {op.lastName}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={sectionHeaderStyle}>Presenti</div>
          <div style={{ padding: '4px 8px' }}>
            {data.attendees.map((a, idx) => (
              <div key={idx} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#1a2942', padding: '1px 0' }}>
                <StaticCheckbox checked={a.present} />
                <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{a.fullName}</span>
                {a.note && <span style={{ color: '#64748b' }}>({a.note})</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Orario / Mezzi */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', borderBottom: '1px solid #94a3b8' }}>
        <div style={{ flex: 1, minWidth: 0, borderRight: isMobile ? 'none' : '1px solid #94a3b8', borderBottom: isMobile ? '1px solid #94a3b8' : 'none' }}>
          <div style={sectionHeaderStyle}>Ora inizio / fine lavoro</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '4px 8px' }}>
            <div style={{ flex: '1 1 100px', minWidth: 0 }}>
              <label style={fieldLabelStyle}>Inizio</label>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <span style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>{reportDate}</span>
                <div style={{ ...fieldValueStyle, width: 'auto', flex: 1, minWidth: 0 }}>{data.startTime}</div>
              </div>
            </div>
            <div style={{ flex: '1 1 100px', minWidth: 0 }}>
              <label style={fieldLabelStyle}>Fine</label>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <span style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>{reportDate}</span>
                <div style={{ ...fieldValueStyle, width: 'auto', flex: 1, minWidth: 0 }}>{data.endTime}</div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={sectionHeaderStyle}>Mezzi utilizzati</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', padding: '4px 8px' }}>
            {data.vehicles.map(v => (
              <label key={v.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#1a2942' }}>
                <StaticCheckbox checked={v.selected} />
                {v.name}{v.plate ? ` (${v.plate})` : ''}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Firme */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', borderBottom: '1px solid #94a3b8' }}>
        <div style={{ flex: 1, minWidth: 0, borderRight: isMobile ? 'none' : '1px solid #94a3b8', borderBottom: isMobile ? '1px solid #94a3b8' : 'none', padding: '6px 8px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#1a2942', textTransform: 'uppercase', marginBottom: '4px' }}>Firma EGR ({compiledByLabel})</div>
          <div style={{ height: '80px', border: '1px dashed #94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {employeeSignatureUrl ? (
              <img src={employeeSignatureUrl} alt="Firma operatore" style={{ maxHeight: '76px', maxWidth: '100%' }} />
            ) : (
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>Firma non disponibile</span>
            )}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0, padding: '6px 8px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#1a2942', textTransform: 'uppercase', marginBottom: '4px' }}>Firma cliente ({data.customerSignerName})</div>
          <div style={{ height: '80px', border: '1px dashed #94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {data.customerSignatureDataUrl ? (
              <img src={data.customerSignatureDataUrl} alt="Firma cliente" style={{ maxHeight: '76px', maxWidth: '100%' }} />
            ) : (
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>Firma non disponibile</span>
            )}
          </div>
        </div>
      </div>

      {/* Valutazione servizio */}
      <div>
        <div style={sectionHeaderStyle}>Valutazione servizio</div>
        <div style={{ fontSize: '12px', color: '#1a2942', padding: '4px 10px 0' }}>
          Valuti la nostra prestazione, ci aiuterà a migliorare i nostri servizi
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', padding: '4px 10px' }}>
          {SERVICE_RATING_OPTIONS.map(opt => (
            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#1a2942' }}>
              <StaticCheckbox checked={data.serviceRating === opt.value} round />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap', justifyContent: 'space-between', gap: isMobile ? '8px' : '4px 10px', padding: '6px 10px', borderTop: '1px solid #94a3b8', fontSize: '10.5px', color: '#64748b' }}>
        <div>
          <strong style={{ color: '#1a2942' }}>EGR SRL</strong><br />
          Via Grecia, 8 – 70032 Bitonto (BA) — P.IVA 08762090721<br />
          Mail: info@egrsrl.com — Pec: egrsrlbitonto@pec.it — Web: www.ecogrouprizzi.it
        </div>
        <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
          <strong style={{ color: '#1a2942' }}>ASSISTENZA H24</strong><br />
          080 2223343<br />
          Cell. 393 8789920 / 389 0018570
        </div>
      </div>
    </div>
  );
}
